import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Printer, ArrowLeft, Tree, Drop, Butterfly, Recycle, Leaf, CircleNotch,
} from "@phosphor-icons/react";
import { fetchMyFieldSnapshot, fetchImpactField } from "@/lib/api";
import { regionHealth, healthBand } from "@/lib/laymen";

/**
 * FieldReport — druck-optimiertes A4-Layout.
 *
 * Per Browser → „Drucken" → „Als PDF speichern" bekommst du ein
 * sofort teilbares Dokument. Kein Backend-Renderer nötig.
 */
export default function FieldReport() {
  const { fieldId } = useParams();
  const [snap, setSnap] = useState(null);
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, i] = await Promise.all([
          fetchMyFieldSnapshot(fieldId).catch(() => null),
          fetchImpactField(fieldId).catch(() => null),
        ]);
        if (!cancelled) {
          setSnap(s);
          setImpact(i);
          if (!s && !i) setErr("Feld nicht gefunden");
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [fieldId]);

  const field = snap?.field;
  const metrics = snap?.snapshot?.metrics || {};
  const score = regionHealth(metrics);
  const band = healthBand(score);
  const today = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });

  if (loading) return (
    <div className="p-8 flex items-center gap-2"><CircleNotch size={16} className="animate-spin" /> Lade Jahresbilanz …</div>
  );
  if (err) return <div className="p-8 text-[#FF7A45]">{err}</div>;

  return (
    <div className="bg-[#0A0A0A] min-h-screen py-8 print:py-0 print:bg-[#121212]" data-testid="field-report">
      <Helmet>
        <title>Jahresbilanz · {field?.label || "Feld"} · bodenblick.com</title>
      </Helmet>

      {/* Screen controls (hidden on print) */}
      <div className="max-w-[210mm] mx-auto px-4 pb-4 flex items-center justify-between print:hidden">
        <Link to="/myfields" className="text-xs flex items-center gap-1 text-zinc-400 hover:text-white">
          <ArrowLeft size={12} /> Zurück
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="field-report-print"
          className="bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-full px-4 py-2 text-sm flex items-center gap-1"
        >
          <Printer size={14} /> Drucken / als PDF speichern
        </button>
      </div>

      {/* A4 page */}
      <div
        className="bg-[#121212] mx-auto shadow-sm print:shadow-none p-10 text-[12pt]"
        style={{ width: "210mm", minHeight: "297mm" }}
      >
        <header className="flex items-start justify-between border-b border-white/10 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <Leaf size={18} className="text-[#00FA9A]" weight="fill" />
              <div className="text-xs tracking-[0.25em] uppercase text-zinc-400">bodenblick.com</div>
            </div>
            <h1 className="mt-1 text-3xl font-medium tracking-tight">
              Mein Feld in Zahlen
            </h1>
            <div className="text-sm text-zinc-400 mt-1">
              Jahresbilanz · {today}
            </div>
          </div>
          <div className="text-right text-sm text-zinc-400">
            <div>{field?.label}</div>
            <div className="font-mono text-[10pt]">{field?.area_km2} km² · {((field?.area_km2 || 0) * 100).toFixed(2)} ha</div>
          </div>
        </header>

        {/* Health summary */}
        <section className="mt-6 grid grid-cols-2 gap-4">
          <div className="p-4 border border-white/10 rounded-sm">
            <div className="eid-label">Gesamt-Gesundheit</div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="text-4xl font-medium tracking-tight">{score}</div>
              <div className="text-sm text-zinc-400">/100 · {band.label}</div>
            </div>
          </div>
          <div className="p-4 border border-white/10 rounded-sm">
            <div className="eid-label">Ertrags-Ausblick</div>
            <div className="flex items-baseline gap-2 mt-1">
              <div className="text-4xl font-medium tracking-tight">
                {metrics.yield_forecast_index != null ? Math.round(metrics.yield_forecast_index) : "–"}
              </div>
              <div className="text-sm text-zinc-400">/100</div>
            </div>
          </div>
        </section>

        {/* Impact */}
        {impact && (
          <section className="mt-6" data-testid="field-report-impact">
            <h2 className="text-lg font-medium border-b border-white/10 pb-2">
              Was dein Feld bewirkt hat
            </h2>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <ImpactLine icon={Tree} title="CO₂ aus der Luft gebunden"
                value={`${impact.co2.tons_per_year.toLocaleString("de-DE")} t / Jahr`}
                caption={`entspricht ${impact.co2.equivalent_trees_year.toLocaleString("de-DE")} Bäumen`}
              />
              <ImpactLine icon={Recycle} title="Humus-Trend"
                value={`${impact.humus.trend} (${impact.humus.score})`}
                caption={impact.humus.methodology} />
              <ImpactLine icon={Butterfly} title="Biodiversität"
                value={`${impact.biodiversity.score} / 100 · ${impact.biodiversity.label}`}
                caption={impact.biodiversity.methodology} />
              <ImpactLine icon={Drop} title="Wasser nicht bewässert"
                value={`${(impact.water.liters_saved_per_year / 1000).toLocaleString("de-DE", { maximumFractionDigits: 0 })} m³`}
                caption={`≈ ${impact.water.equivalent_showers.toLocaleString("de-DE")} Duschen`}
              />
            </div>
            <div className="mt-4 text-sm italic text-zinc-400 border-l-2 border-[#00FA9A]/40 pl-3 space-y-1">
              {(impact.narrative || []).map((l, i) => <p key={i}>{l}</p>)}
            </div>
          </section>
        )}

        {/* Key metrics */}
        <section className="mt-6">
          <h2 className="text-lg font-medium border-b border-white/10 pb-2">Satellit & Wetter in Zahlen</h2>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mt-3">
            <Row k="NDVI aktuell" v={metrics.ndvi_latest?.toFixed(2) ?? "–"} />
            <Row k="NDVI-Anomalie" v={metrics.ndvi_anomaly?.toFixed(3) ?? "–"} />
            <Row k="Bodenfeuchte-Bilanz" v={metrics.soil_moisture_deficit?.toFixed(3) ?? "–"} />
            <Row k="Forst-Vitalität" v={metrics.forest_vitality_index?.toFixed(0) ?? "–"} />
          </div>
        </section>

        <footer className="mt-10 pt-4 border-t border-white/10 text-[10pt] text-zinc-500 leading-relaxed">
          <p>
            Erzeugt von <strong>bodenblick.com</strong> · Quellen: Copernicus Sentinel-2/3,
            Open-Meteo ERA5, statistische Impact-Modelle (IPCC Tier-1). Keine Gewähr für Zertifizierung.
            DSGVO-konform · keine persönlichen Daten Dritter enthalten.
          </p>
        </footer>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white; }
          @page { size: A4; margin: 12mm; }
        }
      `}</style>
    </div>
  );
}

function ImpactLine({ icon: Icon, title, value, caption }) {
  return (
    <div className="p-3 border border-white/10 rounded-sm">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Icon size={14} className="text-[#00FA9A]" /> {title}
      </div>
      <div className="text-xl font-medium font-mono mt-1">{value}</div>
      {caption && <div className="text-[10pt] text-zinc-400 mt-0.5">{caption}</div>}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between border-b border-white/10 py-1">
      <span className="text-zinc-400">{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}
